import { ReactNode, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import axios from 'axios'
import { Alert, Button, ButtonProps, Empty, Grid, Input, InputNumber, Modal, Spin, Upload } from 'antd'
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleFilled,
  CheckOutlined,
  FileTextOutlined,
  ThunderboltFilled,
  UploadOutlined
} from '@ant-design/icons'
import { createProject, getProjects } from '@/store/slices/projects/thunks'
import { uploadTemplate } from '@/store/slices/template/thunks'
import { setTemplateUploadProgress } from '@/store/slices/template/actions'
import { AppDispatch } from '@/store/store'
import { ProjectDataUploader } from '@pages/projects/ProjectDataUploader'
import { ReportOutline } from '@pages/projects/ReportOutline'
import { ReportRefine, isReady } from '@pages/projects/ReportRefine'
import * as api from '@services/project-service'
import './AiBuilder.less'

const POLLING = ['drafting', 'binding', 'approved', 'filling']

const STEPS = ['Project details', 'Template', 'Data', 'Outline', 'Refine data & layouts', 'Create presentation']

const QUESTIONS = [
  {
    key: 'about',
    label: 'Tell us a bit about the business or the client',
    hint: 'For example: a browser maker that tracks how people choose and rate browsers.'
  },
  {
    key: 'problem',
    label: 'What is the business problem?',
    hint: 'For example: Chrome users are hard to win over; which of them could switch to Safari?'
  },
  {
    key: 'objectives',
    label: 'What are the research objectives?',
    hint: 'For example: size the persuadable group, profile them, and find what would make them switch.'
  },
  {
    key: 'audience',
    label: 'Who will read the presentation?',
    hint: "For example: the client's product leadership team."
  }
]

const COUNTS = [6, 8, 12, 16, 20]

const CREATING = {
  title: 'Creating the presentation',
  steps: ['Copying the layout slides', 'Attaching the data to each chart', 'Writing titles and headlines']
}

const WAITING = {
  drafting: {
    title: 'Drafting the outline',
    steps: ['Reading your brief', 'Scanning the data tables', 'Grouping topics into sections', 'Writing slide topics']
  },
  binding: {
    title: 'Choosing data and layouts',
    steps: [
      'Matching each topic to a table',
      'Choosing rows and columns',
      'Picking a layout for each chart',
      'Checking each choice against the data'
    ]
  },
  approved: CREATING,
  filling: CREATING
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

const errorMessage = (err: any) =>
  err?.response?.data?.detail ?? err?.message ?? (typeof err === 'string' ? err : 'Something went wrong')

const Layout = ({ previous, next, children }) => (
  <>
    {children}
    <div className="ai-builder__inline-actions">
      {previous}
      {next}
    </div>
  </>
)

const NextButton = ({ children, ...props }: ButtonProps) => (
  <Button type="primary" size="large" className="ai-btn-next" {...props}>
    {children}
    <ArrowRightOutlined />
  </Button>
)

const BackButton = ({ children = 'Back', ...props }: ButtonProps) => (
  <Button
    size="large"
    className="ai-btn-back"
    icon={<ArrowLeftOutlined />}
    aria-label={typeof children === 'string' ? children : 'Back'}
    {...props}
  >
    {children}
  </Button>
)

const Pane = ({
  step,
  title,
  intro,
  back,
  next,
  hint,
  children
}: {
  step: number
  title: string
  intro?: ReactNode
  back?: ReactNode
  next?: ReactNode
  hint?: ReactNode
  children: ReactNode
}) => (
  <section className="ai-builder__pane">
    <header className="ai-builder__pane-head">
      <span className="ai-builder__eyebrow">
        Step {step} of {STEPS.length}
      </span>
      <h3>{title}</h3>
      {intro && <p>{intro}</p>}
    </header>
    <div className="ai-builder__pane-body">{children}</div>
    {(back || next) && (
      <footer className="ai-builder__footer">
        <div>{back}</div>
        <div>
          {hint && <span className="ai-builder__hint">{hint}</span>}
          {next}
        </div>
      </footer>
    )}
  </section>
)

const Stepper = ({ current, compact }) =>
  compact ? (
    <div className="ai-stepper is-compact">
      <span className="ai-stepper__compact-label">
        Step {current + 1} of {STEPS.length}: <strong>{STEPS[current]}</strong>
      </span>
      <div className="ai-stepper__bar">
        <span style={{ width: `${((current + 1) / STEPS.length) * 100}%` }} />
      </div>
    </div>
  ) : (
    <ol className="ai-stepper">
      {STEPS.map((title, i) => (
        <li
          key={title}
          className={`ai-stepper__item${i < current ? ' is-done' : ''}${i === current ? ' is-current' : ''}`}
          aria-current={i === current ? 'step' : undefined}
        >
          <span className="ai-stepper__dot">{i < current ? <CheckOutlined /> : i + 1}</span>
          <span className="ai-stepper__label">{title}</span>
          {i < STEPS.length - 1 && (
            <span className="ai-stepper__line">
              <span />
            </span>
          )}
        </li>
      ))}
    </ol>
  )

const AiWorking = ({ status }) => {
  const { title, steps } = WAITING[status]
  const [index, setIndex] = useState(0)
  useEffect(() => {
    const timer = setInterval(() => setIndex(i => (i + 1) % steps.length), 2600)
    return () => clearInterval(timer)
  }, [steps.length])
  return (
    <div className="ai-working" role="status">
      <div className="ai-working__orb">
        <span />
      </div>
      <h4>{title}</h4>
      <p className="ai-working__step" key={index}>
        {steps[index]}…
      </p>
      <div className="ai-working__skeleton" aria-hidden>
        <span style={{ width: '62%' }} />
        <span style={{ width: '88%' }} />
        <span style={{ width: '74%' }} />
      </div>
      <small>This can take a minute or two. Keep this window open.</small>
    </div>
  )
}

const SuccessMark = ({ size = 72 }) => (
  <svg className="ai-success" width={size} height={size} viewBox="0 0 52 52" fill="none" aria-hidden>
    <circle className="ai-success__circle" cx="26" cy="26" r="24" />
    <path className="ai-success__check" d="M15 27l7 7 15-15" />
  </svg>
)

export const Autoreport = ({ visible, hide }) => {
  const history = useHistory()
  const dispatch: AppDispatch = useDispatch()
  const screens = Grid.useBreakpoint()
  const [current, setCurrent] = useState<number>(0)
  const [direction, setDirection] = useState<'forward' | 'back'>('forward')
  const [templateTab, setTemplateTab] = useState<'upload' | 'library'>('upload')
  const [name, setName] = useState<string>('')
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [slideCount, setSlideCount] = useState<number>(12)
  const [file, setFile] = useState<File>(null)
  const [projectId, setProjectId] = useState<number>(null)
  const [template, setTemplate] = useState<File>(null)
  const [templateSlides, setTemplateSlides] = useState<number>(null)
  const [themes, setThemes] = useState<any[]>(null)
  const [theme, setTheme] = useState<number>(null)
  const [dataFiles, setDataFiles] = useState<number[]>([])
  const [uploaderKey, setUploaderKey] = useState(0)
  const [plan, setPlan] = useState<any>(null)
  const [outline, setOutline] = useState<any[]>([])
  const [deck, setDeck] = useState<any[]>([])
  const [tables, setTables] = useState<any[]>([])
  const [layouts, setLayouts] = useState<any[]>([])
  const [error, setError] = useState<string>(null)
  const [busy, setBusy] = useState(false)

  const run = (work: () => Promise<any>) => {
    setBusy(true)
    setError(null)
    work()
      .catch(err => setError(errorMessage(err)))
      .finally(() => setBusy(false))
  }

  const go = (step: number) => {
    setError(null)
    setDirection(step < current ? 'back' : 'forward')
    setCurrent(step)
  }

  const brief = () =>
    QUESTIONS.filter(q => answers[q.key]?.trim())
      .map(q => `${q.label}\n${answers[q.key].trim()}`)
      .join('\n\n')

  const saveDetails = () =>
    run(async () => {
      let id = projectId
      if (id == null) {
        const project = await dispatch(createProject({ projectType: 'shell' })).unwrap()
        id = project.id
        setProjectId(id)
      }
      await api.editProjectName(id, name.trim())
      dispatch(getProjects())
      go(1)
    })

  const sendTemplate = () =>
    run(async () => {
      Object.defineProperty(template, 'name', {
        writable: true,
        value: template.name.replace(/\.[^.]+$/, ext => ext.toLowerCase())
      })
      const result = await dispatch(
        uploadTemplate({ projectId, file: template as any, cancelToken: axios.CancelToken.source().token })
      ).unwrap()
      dispatch(setTemplateUploadProgress(null))
      const slides = result?.templateJson?.slides ?? []
      if (!slides.length) {
        throw new Error('The template has no slides that SlideGen can read.')
      }
      setTemplateSlides(slides.length)
    })

  const loadThemes = () => {
    if (themes == null && projectId != null) {
      api
        .getReportThemes(projectId)
        .then(setThemes)
        .catch(err => setError(errorMessage(err)))
    }
  }

  const chooseTheme = () =>
    run(async () => {
      const result = await api.applyReportTheme(projectId, theme)
      setTemplateSlides(result.slides)
    })

  const draftOutline = () =>
    run(async () => {
      if (!plan) {
        setPlan(await api.createReportPlan(projectId, file, brief(), slideCount))
      }
      go(3)
    })

  const refine = () =>
    run(async () => {
      setPlan(await api.refineReportPlan(projectId, plan.id, outline))
      go(4)
    })

  const create = () =>
    run(async () => {
      setPlan(await api.approveReportPlan(projectId, plan.id, deck))
      go(5)
    })

  useEffect(() => {
    if (current < 3 || !plan || !POLLING.includes(plan.status)) {
      return
    }
    const timer = setTimeout(() => {
      api
        .getReportPlan(projectId)
        .then(latest => latest && setPlan(latest))
        .catch(err => setError(errorMessage(err)))
    }, 3000)
    return () => clearTimeout(timer)
  }, [current, plan, projectId])

  useEffect(() => {
    if (plan?.status === 'outlined') {
      setOutline(plan.slides)
    }
    if (plan?.status === 'ready') {
      setDeck(plan.slides)
      api.getReportTables(projectId, plan.id).then(setTables).catch(err => setError(errorMessage(err)))
      api.getReportLayouts(projectId, plan.id).then(setLayouts).catch(err => setError(errorMessage(err)))
    }
  }, [plan?.status, plan?.id, plan?.slides, projectId])

  const slidesInOutline = outline.filter(i => i.kind === 'slide').length
  const report = plan?.texts ?? []
  const created = report.filter(r => r.created)
  const skipped = report.filter(r => !r.created)
  const sectionsMade = deck.filter(i => i.kind === 'section').length
  const failed = plan?.status === 'failed' && (
    <Alert type="error" showIcon message="The AI step failed." description={plan.error} />
  )

  const steps = [
    <Pane
      key="details"
      step={1}
      title="Tell us about the presentation"
      intro="The AI uses your answers to plan the sections and slides. Only the name is required. The questions have no length limit."
      hint={!name.trim() && 'Add a name to go on'}
      next={
        <NextButton disabled={!name.trim()} loading={busy} onClick={saveDetails}>
          Next
        </NextButton>
      }
    >
      <div className="ai-builder__field">
        <label htmlFor="ai-project-name">
          Name of the presentation<span className="ai-builder__required">*</span>
        </label>
        <Input
          id="ai-project-name"
          size="large"
          placeholder="For example: Browser tracker wave 4"
          maxLength={100}
          value={name}
          onChange={e => setName(e.target.value)}
          onPressEnter={() => name.trim() && !busy && saveDetails()}
          autoFocus
        />
      </div>
      {QUESTIONS.map(q => (
        <div key={q.key} className="ai-builder__field">
          <label htmlFor={`ai-brief-${q.key}`}>{q.label}</label>
          <Input.TextArea
            id={`ai-brief-${q.key}`}
            autoSize={{ minRows: 2, maxRows: 8 }}
            placeholder={q.hint}
            value={answers[q.key] ?? ''}
            onChange={e => setAnswers(a => ({ ...a, [q.key]: e.target.value }))}
          />
        </div>
      ))}
      <div className="ai-builder__row">
        <div className="ai-builder__field">
          <label htmlFor="ai-slide-count">How many data slides?</label>
          <div className="ai-builder__count">
            <InputNumber
              id="ai-slide-count"
              min={1}
              max={60}
              value={slideCount}
              onChange={value => setSlideCount(Number(value) || null)}
            />
            <div className="ai-chips" role="group" aria-label="Quick picks">
              {COUNTS.map(n => (
                <button
                  key={n}
                  type="button"
                  className={`ai-chip${slideCount === n ? ' is-active' : ''}`}
                  aria-pressed={slideCount === n}
                  onClick={() => setSlideCount(n)}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
          <span className="ai-builder__help">The cover and the section dividers come in addition.</span>
        </div>
        <div className="ai-builder__field">
          <label>Instructions document (optional)</label>
          <div className="ai-dropzone--small">
            <Upload.Dragger
              accept=".docx"
              maxCount={1}
              beforeUpload={f => {
                setFile(f)
                return false
              }}
              onRemove={() => setFile(null)}
            >
              <p className="ant-upload-text">
                <FileTextOutlined /> Drop a .docx or click to choose
              </p>
            </Upload.Dragger>
          </div>
        </div>
      </div>
    </Pane>,
    <Pane
      key="template"
      step={2}
      title="Choose the template"
      intro="The template gives the look of the presentation: its cover, section divider and chart slides. Upload a .pptx or choose one from the library."
      back={<BackButton onClick={() => go(0)} />}
      hint={templateSlides == null && 'Upload or choose a template to go on'}
      next={
        <NextButton disabled={templateSlides == null} onClick={() => go(2)}>
          Next
        </NextButton>
      }
    >
      {templateSlides != null ? (
        <div className="ai-ready">
          <SuccessMark size={40} />
          <div>
            <strong>Template ready</strong>
            <span>{templateSlides} slides can be used as layouts.</span>
          </div>
        </div>
      ) : (
        <>
          <div className="ai-segmented" role="tablist">
            <span
              className="ai-segmented__thumb"
              style={{ transform: templateTab === 'library' ? 'translateX(100%)' : 'none' }}
            />
            <button
              type="button"
              role="tab"
              aria-selected={templateTab === 'upload'}
              onClick={() => setTemplateTab('upload')}
            >
              Upload
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={templateTab === 'library'}
              onClick={() => {
                setTemplateTab('library')
                loadThemes()
              }}
            >
              Choose from library
            </button>
          </div>
          {templateTab === 'upload' ? (
            <div className="ai-builder__pane-body ai-fade" key="upload">
              <Upload.Dragger
                accept=".pptx"
                maxCount={1}
                disabled={busy}
                beforeUpload={f => {
                  setTemplate(f)
                  return false
                }}
                onRemove={() => setTemplate(null)}
              >
                <p className="ant-upload-drag-icon">
                  <UploadOutlined />
                </p>
                <p className="ant-upload-text">Drop the .pptx here, or click to choose it</p>
                <p className="ant-upload-hint">SlideGen reads each slide of the file as a layout.</p>
              </Upload.Dragger>
              <div>
                <Button type="primary" size="large" disabled={!template} loading={busy} onClick={sendTemplate}>
                  Upload template
                </Button>
              </div>
            </div>
          ) : (
            <div className="ai-builder__pane-body ai-fade" key="library">
              {themes == null ? (
                <Spin />
              ) : themes.length ? (
                <>
                  <div className="ai-builder__themes">
                    {themes.map(t => (
                      <button
                        key={t.id}
                        type="button"
                        className={`ai-theme${theme === t.id ? ' is-selected' : ''}`}
                        aria-pressed={theme === t.id}
                        onClick={() => setTheme(t.id)}
                      >
                        {theme === t.id && <CheckCircleFilled className="ai-theme__check" />}
                        <strong>{t.name}</strong>
                        <span>{t.slides} slides</span>
                      </button>
                    ))}
                  </div>
                  <div>
                    <Button type="primary" size="large" disabled={theme == null} loading={busy} onClick={chooseTheme}>
                      Use this template
                    </Button>
                  </div>
                </>
              ) : (
                <Empty description="The library has no templates yet. Upload a .pptx instead." />
              )}
            </div>
          )}
        </>
      )}
    </Pane>,
    <Pane
      key="data"
      step={3}
      title="Upload the data"
      intro="Choose the table reader for the file, then add one data file at a time. You can add more than one file."
      back={<BackButton onClick={() => go(1)} />}
      hint={!dataFiles.length && 'Add at least one data file'}
      next={
        <NextButton disabled={!dataFiles.length} loading={busy} onClick={draftOutline}>
          Draft the outline
        </NextButton>
      }
    >
      <ProjectDataUploader
        key={uploaderKey}
        projectId={projectId}
        onComplete={(id: number) => {
          setDataFiles(files => [...files, id])
          setUploaderKey(k => k + 1)
        }}
        onCancel={() => setUploaderKey(k => k + 1)}
        Layout={Layout}
      />
      {dataFiles.length > 0 && (
        <div className="ai-files">
          {dataFiles.map((id, i) => (
            <span key={id} className="ai-file-chip">
              <CheckCircleFilled /> Data file {i + 1} read
            </span>
          ))}
        </div>
      )}
    </Pane>,
    <Pane
      key="outline"
      step={4}
      title="Shape the story"
      intro={
        plan?.status === 'drafting'
          ? null
          : 'The AI drafted these sections and slides. Rename, add, delete or drag them until the story is right. Each slide shows one table as one chart.'
      }
      back={plan?.status !== 'drafting' && <BackButton onClick={() => go(2)} />}
      hint={['outlined', 'ready'].includes(plan?.status) && !slidesInOutline && 'Add at least one slide'}
      next={
        ['outlined', 'ready'].includes(plan?.status) && (
          <NextButton disabled={!slidesInOutline} loading={busy} onClick={refine}>
            Choose data and layouts
          </NextButton>
        )
      }
    >
      {failed}
      {plan?.status === 'drafting' && <AiWorking status="drafting" />}
      {['outlined', 'ready'].includes(plan?.status) && <ReportOutline items={outline} onChange={setOutline} />}
    </Pane>,
    <Pane
      key="refine"
      step={5}
      title="Refine data and layouts"
      intro={
        plan?.status === 'ready'
          ? 'Check the table, rows, columns and layout of each slide, and edit the headlines. Hover a status to see why the AI chose it.'
          : null
      }
      back={plan?.status !== 'binding' && <BackButton onClick={() => go(3)}>Back to outline</BackButton>}
      hint={plan?.status === 'ready' && !deck.some(isReady) && 'Give at least one slide its data'}
      next={
        plan?.status === 'ready' && (
          <NextButton disabled={!deck.some(isReady)} loading={busy} onClick={create}>
            Create presentation
          </NextButton>
        )
      }
    >
      {failed}
      {plan?.status === 'binding' && <AiWorking status="binding" />}
      {plan?.status === 'ready' && <ReportRefine items={deck} tables={tables} layouts={layouts} onChange={setDeck} />}
    </Pane>,
    <Pane
      key="create"
      step={6}
      title="Create presentation"
      back={plan?.status === 'filled' && <Button size="large" onClick={hide}>Close</Button>}
      next={
        plan?.status === 'filled' && (
          <NextButton onClick={() => history.push(`/app/${projectId}`)}>Open project</NextButton>
        )
      }
    >
      {failed}
      {WAITING[plan?.status] && <AiWorking status={plan.status} />}
      {plan?.status === 'filled' && (
        <div className="ai-result">
          <SuccessMark size={84} />
          <h4>{plural(created.length, 'slide')} created</h4>
          <p>
            {skipped.length
              ? 'Open the project to check the slides and edit them.'
              : 'Every slide in the outline was created. Open the project to check them.'}
          </p>
          <div className="ai-result__stats">
            <span>
              <strong>{created.length}</strong> {created.length === 1 ? 'data slide' : 'data slides'}
            </span>
            <span>
              <strong>{sectionsMade}</strong> {sectionsMade === 1 ? 'section' : 'sections'}
            </span>
            {skipped.length > 0 && (
              <span>
                <strong>{skipped.length}</strong> left out
              </span>
            )}
          </div>
          {skipped.length > 0 && (
            <div className="ai-result__skipped">
              <strong>Left out of the presentation</strong>
              <ul>
                {skipped.map(s => (
                  <li key={s.key ?? s.title}>
                    {s.title}
                    <em>{s.reason}</em>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Pane>
  ]

  return (
    <Modal
      title={
        <span className="ai-builder__title">
          <span className="ai-builder__spark">
            <ThunderboltFilled />
          </span>
          Build with AI
        </span>
      }
      visible={visible}
      onCancel={hide}
      afterClose={() => {
        setCurrent(0)
        setDirection('forward')
        setTemplateTab('upload')
        setName('')
        setAnswers({})
        setSlideCount(12)
        setFile(null)
        setProjectId(null)
        setTemplate(null)
        setTemplateSlides(null)
        setThemes(null)
        setTheme(null)
        setDataFiles([])
        setUploaderKey(k => k + 1)
        setPlan(null)
        setOutline([])
        setDeck([])
        setTables([])
        setLayouts([])
        setError(null)
      }}
      width="min(1100px, calc(100vw - 32px))"
      style={{ top: 24 }}
      maskStyle={{ background: 'rgba(23, 19, 33, 0.45)', backdropFilter: 'blur(3px)' }}
      className="ai-builder"
      keyboard={false}
      maskClosable={false}
      footer={null}
    >
      <Stepper current={current} compact={screens.md === false} />
      <div className={`ai-builder__body is-${direction}`} key={current}>
        {error && <Alert type="error" showIcon closable message={error} onClose={() => setError(null)} />}
        {steps[current]}
      </div>
    </Modal>
  )
}
